import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Copy, Loader2 } from "lucide-react";
import {
  getTransformadPriceUpdateEmailDataPriceUpdateTransformedPriceUpdateEmailDataGet,
  GetTransformadPriceUpdateEmailDataPriceUpdateTransformedPriceUpdateEmailDataGetData,
  type PriceUpdateEmailDataInputRow,
} from "@/client-admin";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/components/ui/use-toast";
import { PriceUpdateEmailTable } from "@/components/PriceUpdate/PriceUpdateEmailTable/PriceUpdateEmailTable";

export function ComparisonDialog() {
  const [activeTab, setActiveTab] = useState("emailData");
  const { toast } = useToast();

  const queryParams: GetTransformadPriceUpdateEmailDataPriceUpdateTransformedPriceUpdateEmailDataGetData = {};

  const { data, isLoading, error } = useQuery({
    queryKey: ["transformedPriceUpdateData", queryParams],
    queryFn: () => getTransformadPriceUpdateEmailDataPriceUpdateTransformedPriceUpdateEmailDataGet(queryParams),
  });

  const copyToClipboard = () => {
    if (data) {
      navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      toast({
        title: "Copied to clipboard",
        description: "The raw data has been copied to your clipboard",
      });
    }
  };

  // Function to extract table data for a specific category
  const getEmailTableData = (category: string, field?: string): PriceUpdateEmailDataInputRow[] => {
    if (!data?.email_input) return [];

    const emailInput = data.email_input;
    const fieldKey = field ? `${category}_${field}` : category;
    const fieldData = emailInput[fieldKey as keyof typeof emailInput];

    if (!fieldData) return [];

    // Convert the object to an array of rows
    return Object.entries(fieldData)
      .map(([_, rowData]) => rowData as PriceUpdateEmailDataInputRow)
      .filter((row): row is PriceUpdateEmailDataInputRow => !!row);
  };

  const electricityBaseAskData = getEmailTableData("electricity_nl", "base_ask");
  const electricityBaseBidData = getEmailTableData("electricity_nl", "base_bid");
  const electricityPeakAskData = getEmailTableData("electricity_nl", "peak_ask");
  const electricityPeakBidData = getEmailTableData("electricity_nl", "peak_bid");
  const gasTtfAskData = getEmailTableData("gas_ttf", "ask");
  const gasTtfBidData = getEmailTableData("gas_ttf", "bid");
  const emissionsData = getEmailTableData("emissions");

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="secondary">
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading comparison...
            </>
          ) : (
            `Compare to Previous Prices`
          )}
        </Button>
      </DialogTrigger>

      <DialogContent className="flex h-[95vh] max-w-screen-lg flex-col justify-start gap-2 overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Price Update Email Data</DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col space-y-4">
          <TabsList className="self-start">
            <TabsTrigger value="emailData">Email Data</TabsTrigger>
            <TabsTrigger value="rawJson">Raw Comparison JSON</TabsTrigger>
          </TabsList>

          <TabsContent value="emailData" className="flex-1">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="mr-2 h-6 w-6 animate-spin" />
                Loading email data...
              </div>
            ) : error ? (
              <div className="py-8 text-center text-red-500">Error loading data: {(error as Error).message}</div>
            ) : (
              <div className="flex flex-col gap-4">
                <PriceUpdateEmailTable
                  data={electricityBaseBidData}
                  title="Electricity NL Base Bid"
                  product="electricity_nl_base_bid"
                />
                <PriceUpdateEmailTable
                  data={electricityBaseAskData}
                  title="Electricity NL Base Ask"
                  product="electricity_nl_base_ask"
                />
                <PriceUpdateEmailTable
                  data={electricityPeakBidData}
                  title="Electricity NL Peak Bid"
                  product="electricity_nl_peak_bid"
                />
                <PriceUpdateEmailTable
                  data={electricityPeakAskData}
                  title="Electricity NL Peak Ask"
                  product="electricity_nl_peak_ask"
                />
                <PriceUpdateEmailTable data={gasTtfBidData} title="Gas TTF Bid" product="gas_ttf_bid" />
                <PriceUpdateEmailTable data={gasTtfAskData} title="Gas TTF Ask" product="gas_ttf_ask" />
                <PriceUpdateEmailTable data={emissionsData} title="Emissions" product="emissions" />
              </div>
            )}
          </TabsContent>

          <TabsContent value="rawJson" className="flex-1">
            <div className="relative rounded-md border bg-muted p-4">
              <Button variant="outline" size="sm" className="absolute right-2 top-2" onClick={() => copyToClipboard()}>
                <Copy className="h-4 w-4" />
              </Button>
              <pre className="max-h-[70vh] overflow-auto text-xs">
                {isLoading
                  ? "Loading..."
                  : error
                    ? `Error: ${(error as Error).message}`
                    : JSON.stringify(data, null, 2)}
              </pre>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
